import type {SearchParams} from 'typings/portaro.be.types';
import {includesAllFrom} from 'shared/utils/array-utils';
import {isEmptyObject} from 'shared/utils/custom-utils';
import {isEqual} from 'lodash-es';
import {isString} from 'shared/utils/string-utils';

type ParamEntry = [ParamName, ParamValue];
type ParamName = keyof SearchParams;
type ParamValue = SearchParams[keyof SearchParams];

export function someSpecifiedParamsAreEmpty(params: SearchParams, specifiedParamNames?: string[]): boolean {

    if (isEmptyObject(params)) {
        return true;
    }

    const paramValues = getParamValues(getParamEntries(params, specifiedParamNames ?? Object.keys(params)));

    if (paramValues.length === 0) {
        return true;
    }

    return paramValues.some((value) => isEmptyValue(value));
}

export function specifiedParamsHaveChanged(oldParams: SearchParams, newParams: SearchParams, specifiedParamNames: string[]) {
    const newParamEntries: ParamEntry[] = getParamEntries(newParams, specifiedParamNames);
    const oldParamEntries: ParamEntry[] = getParamEntries(oldParams, specifiedParamNames);

    if (!identicalParamNames(newParamEntries, oldParamEntries)) {
        return true;
    }

    const newParamsObj = Object.fromEntries(newParamEntries);
    const oldParamsObj = Object.fromEntries(oldParamEntries);

    return !isEqual(newParamsObj, oldParamsObj);
}

function getParamEntries(params: SearchParams, specifiedParamNames: string[]): ParamEntry[] {
    return (Object.entries(params) as ParamEntry[])
        .filter(([paramName,]) => specifiedParamNames.includes(paramName));
}

function isEmptyValue(val: ParamValue): boolean {
    if (val === null) {
        return true;
    }

    if (val === undefined) {
        return true;
    }

    if (isString(val)) {
        return val.length === 0;
    }

    if (typeof val === 'number') {
        return false;
    }

    if (val instanceof Array) {
        return val.length === 0;
    }

    return false;
}

function getParamNames(paramEntries: ParamEntry[]): ParamName[] {
    return paramEntries.map(([paramName,]) => paramName);
}

function getParamValues(paramEntries: ParamEntry[]): ParamValue[] {
    return paramEntries.map(([, paramValue]) => paramValue);
}

function includesAllParamNamesFrom(paramEntries1: ParamEntry[], paramEntries2: ParamEntry[]) {
    return includesAllFrom(getParamNames(paramEntries1), getParamNames(paramEntries2));
}

function identicalParamNames(paramEntries1: ParamEntry[], paramEntries2: ParamEntry[]) {
    return includesAllParamNamesFrom(paramEntries1, paramEntries2)
        && includesAllParamNamesFrom(paramEntries2, paramEntries1);
}
